/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import { EllipsisVertical, LogOut, Plus, Repeat, X } from 'lucide-react';
import { authSignOut } from 'modules/auth/store/actions';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { resetOrganizer } from 'modules/organizer/store/orginizer/actions';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getCollapsibleAnimationStyles, getIconTransitionStyles } from 'shared/utils';
import { AppDispatch } from 'store/store';
import UDTippy from 'ud-ui/components/tippy';
import type { DivProps } from '../../../../../../typings';
import Text from '../../../text';
import { MenuSkeletonBlock } from '../../styles';
import MenuItem from '../menu-item/menu-item';
import * as S from './styles';

interface IProps extends DivProps {
  logo?: string;
  name?: string;
  loading?: boolean;
}

let isFirstRender = true;

const ProfileMenu = React.memo(function Profile(props: IProps) {
  const { logo, name, loading, ...rest } = props;
  const [isOpen, setIsOpen] = useState(() => {
    if (isFirstRender) {
      isFirstRender = false;
      localStorage.setItem('profileMenuOpen', 'false');
      return false;
    }
    const storedState = localStorage.getItem('profileMenuOpen');
    return storedState ? JSON.parse(storedState) : false;
  });

  const [isClosing, setIsClosing] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const onLogout = useCallback(() => {
    dispatch(authSignOut());
    dispatch(resetOrganizer());
  }, [dispatch]);

  const onCreateNewOrganizer = useCallback(() => {
    // TODO: Add get available tournaments type
    // const availableTournamentTypes = getAvailableTournamentTypes(availableTournaments);
    // if (availableTournamentTypes.length === 0) {
    //   toast.warn('Вы можете создать только одну организацию на один вид спорта');
    //   return;
    // }
    navigate(Pages.ORGANIZER.CREATE);
  }, [navigate]);

  const onChangeProfile = useCallback(() => {
    navigate(Pages.SELECT_TOURNAMENT);
  }, [navigate]);

  // Handle opening and closing with animation
  const handleToggle = useCallback(() => {
    if (isOpen) {
      // Start closing animation immediately
      setIsClosing(true);
      setIsOpen(false);
      localStorage.setItem('profileMenuOpen', 'false');
      setIsClosing(false);
    } else {
      setIsOpen(true);
      localStorage.setItem('profileMenuOpen', 'true');
    }
  }, [isOpen]);

  if (loading) {
    return (
      <S.MainBlock {...rest} ref={profileRef} className="pr-2 pl-10">
        <MenuSkeletonBlock style={{ width: '100%', height: 36 }} className="skeleton-block" />
      </S.MainBlock>
    );
  }

  return (
    <S.MainBlock {...rest} ref={profileRef}>
      <S.Profile onClick={() => handleToggle()} className={classNames({ active: isOpen || isClosing })}>
        <S.Icon src={logo ?? USER_DEFAULT_AVATAR} alt={'Logo'} className={classNames({ 'bg-white': !Boolean(logo) })} />
        <S.Name>
          <Text type={'subhead'} color={'inherit'} style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {name ?? 'Неизвестно'}
          </Text>
          {(() => {
            const { containerStyle, activeIconStyle, inactiveIconStyle } = getIconTransitionStyles(isOpen || isClosing);
            return (
              <UDTippy content={isOpen ? 'Скрыть' : 'Раскрыть'} placement={'top'}>
                <div style={{ ...containerStyle, minWidth: 16 }}>
                  <X size={16} style={{ ...activeIconStyle, minWidth: 16, height: 16, fill: 'currentColor' }} />
                  <EllipsisVertical size={16} style={{ ...inactiveIconStyle, minWidth: 16, height: 16, fill: 'currentColor' }} />
                </div>
              </UDTippy>
            );
          })()}
        </S.Name>
      </S.Profile>
      <S.MenuItems style={getCollapsibleAnimationStyles(isOpen || isClosing, '118px')}>
        <MenuItem icon={<Repeat size={16} />} label={'Сменить организатора'} onClick={onChangeProfile} />

        <MenuItem
          checkPath={Pages.ORGANIZER.CREATE}
          icon={<Plus size={16} />}
          label={'Создать новый'}
          onClick={onCreateNewOrganizer}
        />

        <MenuItem icon={<LogOut size={16} />} label={'Выйти'} onClick={onLogout} />
      </S.MenuItems>
    </S.MainBlock>
  );
});

export default ProfileMenu;
